import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import useGetChangeSet from '../../api/useGetChangeSet';
import useReviewChangeSet from '../../api/useReviewChangeSet';
import { PageWrapper, PageContainer } from '../../components/common/PageWrapper.styles';
import { MainContent } from '../../components/common/Layout.styles';
import { PaginationButton } from '../../components/shared/Button.styles';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { ChangelogPage, Error } from './ChangeLogPage.styles';
import { useAuthStore } from '../../context/AuthContext';
import { ChangeSetStatusEnum } from '../../enums/ChangeSetStatusEnum';
import { formatDateTime } from '../../utils/dateFormatters';
import { ChangeSetStatusEnum as StatusEnum } from '../../enums/ChangeSetStatusEnum';
import {
    ButtonContainer,
    ContentContainer,
    HeaderSection,
    HeaderTitle,
    ItemName,
    BadgeRow,
    Badge,
    HeaderDetails,
    DetailGroup,
    DetailGroupLabel,
    FieldRow,
    FieldLabel,
    FieldValue,
    UserAvatarSmall,
    UserFieldCell,
    ChangesSection,
    SectionHeader,
    SectionContainer,
    ChangeItem,
    ChangeBox,
    ChangeBoxContent,
    BoldText,
    ColoredLabel,
    ChangedFieldContainer,
    ChangedFieldBox,
    ValueList,
    ArrayGrid,
    ArrayCard,
    SuccessMessage,
    ErrorMessage,
} from './ChangeSetDetailPage.styles';
import { getInitials } from '../../utils/userFormatters';

interface Difference {
    path: string;
    oldValue: any;
    newValue: any;
    isAdded: boolean;
    isRemoved: boolean;
    isChanged: boolean;
}

const ChangeSetDetailPage = () => {
    const [searchParams] = useSearchParams();
    const changeLogId = searchParams.get('changeLogId');
    const navigate = useNavigate();
    const { changeSet, loading, error, getChangeSet } = useGetChangeSet();
    const { reviewChangeSet, loading: reviewing, error: reviewError, success: reviewSuccess } = useReviewChangeSet();
    const { canEdit } = useAuthStore();
    const [differences, setDifferences] = useState<Difference[]>([]);
    const [targetItemId, setTargetItemId] = useState<number | string | null>(null);

    useEffect(() => {
        if (changeLogId) {
            getChangeSet(parseInt(changeLogId, 10));
        }
    }, [changeLogId]);

    // Parse differences from old/new values
    useEffect(() => {
        if (changeSet && (changeSet.oldValues || changeSet.newValues)) {
            try {
                const oldObj = changeSet.oldValues ? JSON.parse(changeSet.oldValues) : {};
                const newObj = changeSet.newValues ? JSON.parse(changeSet.newValues) : {};

                const diffs = calculateDifferences(oldObj, newObj);
                setDifferences(diffs);

                // Extract the ID of the changed item from old or new values
                const itemId = newObj?.Id ?? oldObj?.Id ?? newObj?.id ?? oldObj?.id ?? null;
                setTargetItemId(itemId);
            } catch (err) {
                console.error('Error parsing changeset values:', err);
            }
        }
    }, [changeSet?.id, changeSet?.oldValues, changeSet?.newValues]);

    /**
     * Deep-filter an object or array to only include the parts that differ between old and new.
     * For arrays of objects with an Id/id field, matches entries by Id and filters sub-properties.
     * Returns null if there are no differences.
     */
    const getChangedParts = (oldVal: any, newVal: any): { filteredOld: any; filteredNew: any } | null => {
        // Primitives or type mismatch — return as-is
        if (typeof oldVal !== typeof newVal || typeof oldVal !== 'object' || oldVal === null || newVal === null) {
            return JSON.stringify(oldVal) !== JSON.stringify(newVal) ? { filteredOld: oldVal, filteredNew: newVal } : null;
        }

        // Arrays
        if (Array.isArray(oldVal) && Array.isArray(newVal)) {
            // Try matching by Id/id
            const hasIds = oldVal.length > 0 && oldVal.some((item) => item && (item.Id !== undefined || item.id !== undefined));
            if (hasIds) {
                const getItemId = (item: any) => item?.Id ?? item?.id;
                const oldMap = new Map(oldVal.map((item) => [getItemId(item), item]));
                const newMap = new Map(newVal.map((item) => [getItemId(item), item]));
                const allIds = new Set([...oldMap.keys(), ...newMap.keys()]);
                const changedOldEntries: any[] = [];
                const changedNewEntries: any[] = [];

                allIds.forEach((id) => {
                    const oldItem = oldMap.get(id);
                    const newItem = newMap.get(id);
                    if (!oldItem && newItem) {
                        changedNewEntries.push(newItem);
                    } else if (oldItem && !newItem) {
                        changedOldEntries.push(oldItem);
                    } else if (oldItem && newItem && JSON.stringify(oldItem) !== JSON.stringify(newItem)) {
                        const subDiff = getChangedParts(oldItem, newItem);
                        if (subDiff) {
                            changedOldEntries.push(subDiff.filteredOld);
                            changedNewEntries.push(subDiff.filteredNew);
                        }
                    }
                });

                if (changedOldEntries.length === 0 && changedNewEntries.length === 0) return null;
                return { filteredOld: changedOldEntries, filteredNew: changedNewEntries };
            }

            // Non-id arrays: fall back to stringify comparison
            if (JSON.stringify(oldVal) === JSON.stringify(newVal)) return null;
            return { filteredOld: oldVal, filteredNew: newVal };
        }

        // Fields that always differ on updates and add noise — skip them
        const ignoredFields = new Set(['LastUpdatedUtc', 'lastUpdatedUtc', 'LastUpdated', 'lastUpdated']);

        // Plain objects — compare property by property, recursively deep-diff changed ones
        const filteredOld: any = {};
        const filteredNew: any = {};
        const allObjKeys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);
        let hasChange = false;

        allObjKeys.forEach((key) => {
            if (ignoredFields.has(key)) return;
            const ov = oldVal[key];
            const nv = newVal[key];
            if (JSON.stringify(ov) !== JSON.stringify(nv)) {
                // Recursively deep-diff nested values
                if (typeof ov === 'object' && typeof nv === 'object' && ov !== null && nv !== null) {
                    const subDiff = getChangedParts(ov, nv);
                    if (subDiff) {
                        filteredOld[key] = subDiff.filteredOld;
                        filteredNew[key] = subDiff.filteredNew;
                        hasChange = true;
                    }
                } else {
                    filteredOld[key] = ov;
                    filteredNew[key] = nv;
                    hasChange = true;
                }
            }
        });

        return hasChange ? { filteredOld, filteredNew } : null;
    };

    const calculateDifferences = (oldObj: any, newObj: any): Difference[] => {
        const diffs: Difference[] = [];
        // Fields that always differ on updates and provide no useful information
        const topLevelIgnored = new Set(['LastUpdatedUtc', 'lastUpdatedUtc', 'LastUpdated', 'lastUpdated']);
        const allKeys = new Set([...Object.keys(oldObj || {}), ...Object.keys(newObj || {})]);

        allKeys.forEach((key) => {
            if (topLevelIgnored.has(key)) return;
            const oldValue = oldObj?.[key];
            const newValue = newObj?.[key];

            if (oldValue === undefined && newValue !== undefined) {
                // Added
                diffs.push({
                    path: key,
                    oldValue: null,
                    newValue,
                    isAdded: true,
                    isRemoved: false,
                    isChanged: false,
                });
            } else if (oldValue !== undefined && newValue === undefined) {
                // Removed
                diffs.push({
                    path: key,
                    oldValue,
                    newValue: null,
                    isAdded: false,
                    isRemoved: true,
                    isChanged: false,
                });
            } else if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
                // Changed — deep filter to only include actually-changed parts
                const changedParts = getChangedParts(oldValue, newValue);
                if (changedParts) {
                    diffs.push({
                        path: key,
                        oldValue: changedParts.filteredOld,
                        newValue: changedParts.filteredNew,
                        isAdded: false,
                        isRemoved: false,
                        isChanged: true,
                    });
                }
            }
        });

        return diffs;
    };

    const getPresentationTitle = (path: string): string => {
        // Convert camelCase or PascalCase to Title Case
        return path
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    };

    const renderValue = (value: any, fieldPath?: string, depth = 0): JSX.Element => {
        if (value === null || value === undefined) {
            return <span>N/A</span>;
        }
        if (typeof value === 'boolean') {
            return <span>{value ? 'Yes' : 'No'}</span>;
        }

        // Check if this is a date field and format it
        const dateFields = ['lastupdatedutc', 'lastupdated', 'expirydate', 'datetimeutc', 'reviewdatetimeutc', 'date', 'createdat', 'updatedat'];
        const normalizedFieldPath = fieldPath?.toLowerCase().replace(/\s+/g, '');
        if (normalizedFieldPath && dateFields.some((df) => normalizedFieldPath.endsWith(df))) {
            // Check if value looks like a date string
            if (typeof value === 'string' && !isNaN(Date.parse(value))) {
                return <span>{formatDateTime(value)}</span>;
            }
        }

        if (typeof value === 'object' && !Array.isArray(value)) {
            const entries = Object.entries(value);
            const isLeaf = entries.every(([, val]) => typeof val !== 'object' || val === null);
            const content = entries.map(([key, val]) => (
                <div key={key}>
                    <BoldText>{getPresentationTitle(key)}: </BoldText>
                    {typeof val === 'object' ? renderValue(val, key, depth + 1) : <span>{String(val)}</span>}
                </div>
            ));

            return isLeaf ? <ArrayCard>{content}</ArrayCard> : <div>{content}</div>;
        }
        if (Array.isArray(value)) {
            const hasObjects = value.some((item) => typeof item === 'object' && item !== null && !Array.isArray(item));
            if (hasObjects) {
                const isLeafArray = value.every(
                    (item) =>
                        typeof item !== 'object' ||
                        item === null ||
                        (typeof item === 'object' &&
                            !Array.isArray(item) &&
                            Object.values(item).every((v) => typeof v !== 'object' || v === null))
                );
                return (
                    <ArrayGrid>
                        {value.map((item, idx) =>
                            typeof item === 'object' && item !== null && !Array.isArray(item) ? (
                                isLeafArray ? (
                                    <ArrayCard key={idx}>
                                        {Object.entries(item).map(([key, val]) => (
                                            <div key={key}>
                                                <BoldText>{getPresentationTitle(key)}: </BoldText>
                                                {typeof val === 'object' ? renderValue(val, key, depth + 1) : <span>{String(val)}</span>}
                                            </div>
                                        ))}
                                    </ArrayCard>
                                ) : (
                                    <div key={idx}>
                                        {Object.entries(item).map(([key, val]) => (
                                            <div key={key}>
                                                <BoldText>{getPresentationTitle(key)}: </BoldText>
                                                {typeof val === 'object' ? renderValue(val, key, depth + 1) : <span>{String(val)}</span>}
                                            </div>
                                        ))}
                                    </div>
                                )
                            ) : typeof item === 'object' ? (
                                <div key={idx}>{renderValue(item, undefined, depth + 1)}</div>
                            ) : (
                                <span key={idx}>{String(item)}</span>
                            )
                        )}
                    </ArrayGrid>
                );
            }
            return (
                <span>
                    {value.map((item, idx) => (
                        <span key={idx}>
                            {typeof item === 'object' ? renderValue(item, undefined, depth + 1) : <span>{String(item)}</span>}
                            {idx < value.length - 1 && <span>, </span>}
                        </span>
                    ))}
                </span>
            );
        }
        return <span>{String(value)}</span>;
    };

    const handleBack = () => {
        navigate('/changelog');
    };

    const handleConfirmReview = async () => {
        if (!changeSet) return;

        try {
            await reviewChangeSet(changeSet);
            // Refresh the changeset data after successful review
            if (changeLogId) {
                getChangeSet(parseInt(changeLogId, 10));
            }
        } catch (err) {
            // Error is already handled by useReviewChangeSet
            console.error('Failed to confirm review:', err);
        }
    };

    const canConfirmReview = () => {
        return canEdit && changeSet?.status === ChangeSetStatusEnum.PendingReview && !reviewing;
    };

    if (loading) {
        return (
            <PageWrapper>
                <PageContainer>
                    <ChangelogPage as={MainContent}>
                        <LoadingSpinner text="Loading changeset details..." />
                    </ChangelogPage>
                </PageContainer>
            </PageWrapper>
        );
    }

    if (error) {
        return (
            <PageWrapper>
                <PageContainer>
                    <ChangelogPage as={MainContent}>
                        <Error>Error loading changeset: {error}</Error>
                        <PaginationButton onClick={handleBack} style={{ marginTop: '1rem' }}>
                            <FontAwesomeIcon icon={faChevronLeft} /> Back to list
                        </PaginationButton>
                    </ChangelogPage>
                </PageContainer>
            </PageWrapper>
        );
    }

    if (!changeSet) {
        return (
            <PageWrapper>
                <PageContainer>
                    <ChangelogPage as={MainContent}>
                        <Error>Changeset not found</Error>
                        <PaginationButton onClick={handleBack} style={{ marginTop: '1rem' }}>
                            <FontAwesomeIcon icon={faChevronLeft} /> Back to list
                        </PaginationButton>
                    </ChangelogPage>
                </PageContainer>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <PageContainer>
                <ChangelogPage as={MainContent}>
                    <ButtonContainer>
                        <PaginationButton onClick={handleBack}>
                            <FontAwesomeIcon icon={faChevronLeft} /> Back to list
                        </PaginationButton>
                        {canEdit && (
                            <PaginationButton onClick={handleConfirmReview} disabled={!canConfirmReview()}>
                                Confirm Review
                            </PaginationButton>
                        )}
                    </ButtonContainer>

                    {/* Success/Error Messages */}
                    {reviewing && <LoadingSpinner text="Saving change set information..." />}
                    {reviewSuccess && (
                        <SuccessMessage>
                            <strong>Success!</strong> Change set information saved.
                        </SuccessMessage>
                    )}
                    {reviewError && (
                        <ErrorMessage>
                            <strong>Error!</strong> {reviewError}
                        </ErrorMessage>
                    )}

                    <ContentContainer>
                        <HeaderSection>
                            <HeaderTitle>
                                <ItemName>
                                    {changeSet.displayInformation}
                                    {targetItemId != null && <span className="item-id">ID: {targetItemId}</span>}
                                </ItemName>
                                <BadgeRow>
                                    <Badge variant="category">Category: {changeSet.categoryName}</Badge>
                                    <Badge variant="type">Type: {changeSet.typeName}</Badge>
                                    <Badge variant={changeSet.status === StatusEnum.Reviewed ? 'reviewed' : 'pending'}>
                                        Status: {changeSet.statusName}
                                    </Badge>
                                </BadgeRow>
                            </HeaderTitle>
                            <HeaderDetails>
                                <DetailGroup>
                                    <DetailGroupLabel>Changed</DetailGroupLabel>
                                    <FieldRow>
                                        <FieldLabel>By:</FieldLabel>
                                        <UserFieldCell>
                                            <UserAvatarSmall>{getInitials(changeSet.userName)}</UserAvatarSmall>
                                            <FieldValue>{changeSet.userName}</FieldValue>
                                        </UserFieldCell>
                                    </FieldRow>
                                    <FieldRow>
                                        <FieldLabel>Date:</FieldLabel>
                                        <FieldValue>{formatDateTime(changeSet.date)}</FieldValue>
                                    </FieldRow>
                                </DetailGroup>
                                {changeSet.reviewerName && (
                                    <DetailGroup variant={changeSet.status === StatusEnum.Reviewed ? 'reviewed' : 'pending'}>
                                        <DetailGroupLabel>Reviewed</DetailGroupLabel>
                                        <FieldRow>
                                            <FieldLabel>By:</FieldLabel>
                                            <UserFieldCell>
                                                <UserAvatarSmall>{getInitials(changeSet.reviewerName)}</UserAvatarSmall>
                                                <FieldValue>{changeSet.reviewerName}</FieldValue>
                                            </UserFieldCell>
                                        </FieldRow>
                                        <FieldRow>
                                            <FieldLabel>Date:</FieldLabel>
                                            <FieldValue>{changeSet.reviewDate ? formatDateTime(changeSet.reviewDate) : 'N/A'}</FieldValue>
                                        </FieldRow>
                                    </DetailGroup>
                                )}
                            </HeaderDetails>
                        </HeaderSection>

                        {/* Changes Section */}
                        <ChangesSection>
                            {differences.length > 0 ? (
                                <>
                                    {/* Removed Fields */}
                                    {differences.filter((d) => d.isRemoved).length > 0 && (
                                        <SectionContainer>
                                            <SectionHeader color="#a94442">Removed Fields</SectionHeader>
                                            {differences
                                                .filter((d) => d.isRemoved)
                                                .map((diff, index) => (
                                                    <ChangeItem key={index}>
                                                        <ChangeBox variant="removed">
                                                            <ChangeBoxContent>
                                                                <BoldText>{getPresentationTitle(diff.path)}: </BoldText>
                                                                {renderValue(diff.oldValue, diff.path)}
                                                            </ChangeBoxContent>
                                                        </ChangeBox>
                                                    </ChangeItem>
                                                ))}
                                        </SectionContainer>
                                    )}

                                    {/* Added Fields */}
                                    {differences.filter((d) => d.isAdded).length > 0 && (
                                        <SectionContainer withPadding>
                                            <SectionHeader color="#3c763d" marginLeft="1rem">
                                                Added Fields
                                            </SectionHeader>
                                            {differences
                                                .filter((d) => d.isAdded)
                                                .map((diff, index) => (
                                                    <ChangeItem key={index}>
                                                        <ChangeBox variant="added">
                                                            <ChangeBoxContent>
                                                                <BoldText>{getPresentationTitle(diff.path)}: </BoldText>
                                                                {renderValue(diff.newValue, diff.path)}
                                                            </ChangeBoxContent>
                                                        </ChangeBox>
                                                    </ChangeItem>
                                                ))}
                                        </SectionContainer>
                                    )}

                                    {/* Changed Fields */}
                                    {differences.filter((d) => d.isChanged).length > 0 && (
                                        <SectionContainer>
                                            <SectionHeader>Changed Fields</SectionHeader>
                                            {differences
                                                .filter((d) => d.isChanged)
                                                .map((diff, index) => (
                                                    <ChangeItem key={index}>
                                                        <ChangedFieldContainer>
                                                            <ChangedFieldBox variant="removed">
                                                                <ColoredLabel color="#a94442">Changed From: </ColoredLabel>
                                                                <ChangeBoxContent>
                                                                    <BoldText>{getPresentationTitle(diff.path)}: </BoldText>
                                                                    {renderValue(diff.oldValue, diff.path)}
                                                                </ChangeBoxContent>
                                                            </ChangedFieldBox>
                                                            <ChangedFieldBox variant="added">
                                                                <ColoredLabel color="#3c763d">Changed To: </ColoredLabel>
                                                                <ChangeBoxContent>
                                                                    <BoldText>{getPresentationTitle(diff.path)}: </BoldText>
                                                                    {renderValue(diff.newValue, diff.path)}
                                                                </ChangeBoxContent>
                                                            </ChangedFieldBox>
                                                        </ChangedFieldContainer>
                                                    </ChangeItem>
                                                ))}
                                        </SectionContainer>
                                    )}
                                </>
                            ) : (
                                <div>No changes detected</div>
                            )}
                        </ChangesSection>
                    </ContentContainer>
                </ChangelogPage>
            </PageContainer>
        </PageWrapper>
    );
};

export default ChangeSetDetailPage;
